/**
 * Author: Romain Hapi, Prenim Ltd
 * Version: 1.0
 * Date: 2026-10-22
 * Project: Prenim Digital
 * Copyright: Prenim Ltd
 */
/**
 * Catalogue des logiciels Prenim, tire des dossiers de commercialisation.
 *
 * Chaque fiche alimente la page `/products/:slug`, la bande de l'accueil et la
 * section « Nos logiciels » des realisations. Les textes reprennent ceux des
 * dossiers de presentation et des plaquettes, en francais et en anglais ; les
 * autres langues du site (es, ar) recoivent l'anglais.
 *
 * Les images (boitiers, captures) vivent sous `public/images/products/` et sont
 * suivies par git. Les PDF vivent sous `public/media/products/`, suivis par git
 * eux aussi ; `scripts/preparer_medias_produits.py` les met a jour depuis les
 * dossiers de commercialisation quand un support change.
 */

const IMG = '/images/products';
const MEDIA = '/media/products';

/** Langue de contenu : le francais, sinon l'anglais. */
export const contentLang = (lang) => (lang === 'fr' ? 'fr' : 'en');

/** Texte d'une entree { fr, en } dans la langue de contenu. */
export const tx = (entry, lang) => {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry;
  return entry[contentLang(lang)] || entry.fr || '';
};

/** Capture propre a chaque langue : `<nom>.fr.webp` / `<nom>.en.webp`. */
const bilingual = (slug, name) => ({
  fr: `${IMG}/${slug}/${name}.fr.webp`,
  en: `${IMG}/${slug}/${name}.en.webp`,
});

/** Capture commune aux deux langues. */
const single = (slug, name) => `${IMG}/${slug}/${name}.webp`;

/** Libelles de la page produit. */
export const PRODUCT_UI = {
  eyebrow: { fr: 'Nos logiciels', en: 'Our software' },
  sectionTitle: { fr: 'Des logiciels conçus pour l’Afrique centrale', en: 'Software built for Central Africa' },
  sectionSubtitle: {
    fr: 'Les produits que Prenim conçoit, installe et accompagne : chacun a sa page, avec ses fonctionnalités, ses captures et ses documents.',
    en: 'The products Prenim designs, deploys and supports: each one has its own page, with its features, screenshots and documents.',
  },
  back: { fr: 'Retour aux réalisations', en: 'Back to our work' },
  discover: { fr: 'Découvrir', en: 'Discover' },
  features: { fr: 'Fonctionnalités', en: 'Features' },
  gallery: { fr: 'Captures d’écran', en: 'Screenshots' },
  galleryNote: {
    fr: 'Captures réelles de la démonstration, données fictives.',
    en: 'Real screenshots from the demo, fictitious data.',
  },
  deployment: { fr: 'Installation', en: 'Deployment' },
  documents: { fr: 'Documents à télécharger', en: 'Downloads' },
  demo: { fr: 'Essayer la démonstration', en: 'Try the demo' },
  contactTitle: { fr: 'Une démonstration, un devis ou une question ?', en: 'A demo, a quote or a question?' },
  contactBody: {
    fr: 'Écrivez-nous à hello@prenim.co.uk, appelez ou écrivez sur WhatsApp à Prenim Tchad au +235 85 20 67 61 ou Prenim Cameroun au +237 6 55 65 51 72, appelez Prenim Ltd (Royaume-Uni) au +44 0113 880 5181, ou rendez-vous sur prenim.co.uk. Déjà client ? Assistance : support@prenim.co.uk.',
    en: 'Email us at hello@prenim.co.uk, call or WhatsApp Prenim Tchad on +235 85 20 67 61 or Prenim Cameroun on +237 6 55 65 51 72, call Prenim Ltd (United Kingdom) on +44 0113 880 5181, or visit prenim.co.uk. Already a client? Support: support@prenim.co.uk.',
  },
  contactCta: { fr: 'Nous écrire', en: 'Contact us' },
  ctaDemo: { fr: 'Demander une démonstration', en: 'Request a demo' },
  ctaQuote: { fr: 'Recevoir un devis', en: 'Get a quote' },
  ctaBrochure: { fr: 'Télécharger la plaquette', en: 'Download the brochure' },
  ctaWhatsappTd: { fr: 'WhatsApp Prenim Tchad', en: 'WhatsApp Prenim Tchad' },
  ctaWhatsappCm: { fr: 'WhatsApp Prenim Cameroun', en: 'WhatsApp Prenim Cameroun' },
  stripTitle: { fr: 'Voyez le logiciel sur vos propres dossiers', en: 'See the software on your own cases' },
  stripBody: {
    fr: 'Démonstration gratuite, en ligne ou dans vos locaux, et devis adapté à votre configuration. Écrivez-nous à hello@prenim.co.uk ou sur WhatsApp, à Prenim Tchad ou Prenim Cameroun.',
    en: 'A free demo, online or at your premises, and a quote tailored to your setup. Email hello@prenim.co.uk or message us on WhatsApp, at Prenim Tchad or Prenim Cameroun.',
  },
  galleryCta: { fr: 'Ces écrans vous parlent ? Parlons de votre projet.', en: 'Like what you see? Let’s talk about your project.' },
};

/** Trois modes d'installation communs aux logiciels de gestion. */
const DEPLOY_ERP = [
  {
    title: { fr: 'Monoposte', en: 'Single-user' },
    body: {
      fr: 'Le logiciel et sa base sur un seul ordinateur, sans dépendre d’Internet.',
      en: 'The software and its database on a single computer, with no need for the internet.',
    },
  },
  {
    title: { fr: 'Client / Serveur', en: 'Client / Server' },
    body: {
      fr: 'Un ordinateur fait serveur ; les postes ouvrent le logiciel dans leur navigateur, sur le réseau local, sans rien installer.',
      en: 'One computer acts as the server; workstations open the software in their browser over the local network, with nothing to install.',
    },
  },
  {
    title: { fr: 'En ligne', en: 'Cloud' },
    body: {
      fr: 'Instance hébergée et sauvegardée par Prenim, ouverte depuis un navigateur, au bureau comme en déplacement.',
      en: 'An instance hosted and backed up by Prenim, opened from a browser, at the office or on the move.',
    },
  },
];

export const PRODUCTS = [
  {
    slug: 'prenim-notary-erp',
    name: 'Prenim Notary ERP',
    category: { fr: 'Notariat', en: 'Notarial practice' },
    tagline: { fr: 'L’étude notariale, entièrement numérique', en: 'The notarial practice, fully digital' },
    summary: {
      fr: 'Dossiers, actes signés et scellés, formalités, comptabilité SYSCOHADA, fonds clients protégés et espace client : tout le travail de l’étude dans un seul logiciel, conçu pour les 17 États de l’espace OHADA.',
      en: 'Files, signed and sealed deeds, formalities, SYSCOHADA accounting, protected client funds and a client portal: all of the practice’s work in a single piece of software, built for the 17 OHADA member states.',
    },
    cover: { fr: single('prenim-notary-erp', 'boitier.fr'), en: single('prenim-notary-erp', 'boitier.en') },
    theme: { from: '#7a1a1f', to: '#4f0f13', accent: '#e6c06a' },
    stats: [
      { value: '17', label: { fr: 'États de l’espace OHADA', en: 'OHADA member states' } },
      { value: '4', label: { fr: 'langues d’interface', en: 'interface languages' } },
      { value: '3', label: { fr: 'modes d’installation', en: 'deployment modes' } },
    ],
    features: [
      {
        title: { fr: 'Tableau de bord', en: 'Dashboard' },
        body: {
          fr: 'Fonds détenus pour les clients, provisions et honoraires du mois, dossiers par étape, actes en attente de formalités, échéances des 15 prochains jours et alertes de sécurité.',
          en: 'Funds held for clients, the month’s retainers and fees, files by stage, deeds awaiting formalities, deadlines for the next 15 days and security alerts.',
        },
      },
      {
        title: { fr: 'Clients et dossiers', en: 'Clients and files' },
        body: {
          fr: 'Répertoire unique des personnes physiques et morales (pièce, RCCM, NIF, représentants), numérotation automatique, plusieurs parties par dossier avec leur qualité, clerc et Notaire référent.',
          en: 'A single register of individuals and companies (ID, RCCM, tax number, representatives), automatic numbering, several parties per file with their capacity, assigned clerk and notary.',
        },
      },
      {
        title: { fr: 'Historique, agenda et courrier', en: 'History, calendar and mail' },
        body: {
          fr: 'Historique complet de chaque dossier, rendez-vous de signature reliés à Google ou Outlook, registre du courrier numéroté et inaltérable, formalités avec échéances calculées et rappels.',
          en: 'Full history of every file, signing appointments linked to Google or Outlook, a numbered and tamper-proof mail register, formalities with computed deadlines and reminders.',
        },
      },
      {
        title: { fr: 'Rédaction des actes', en: 'Deed drafting' },
        body: {
          fr: 'Formulaire guidé et texte de l’acte généré en direct : succession, donation, cession de parts, tutelle, certificats... Vos propres modèles Word importés avec leurs variables, circuit de validation et versions comparées mot à mot.',
          en: 'A guided form with the deed text generated live: succession, gift, share transfer, guardianship, certificates... Your own Word templates imported with their variables, approval workflow and word-by-word version comparison.',
        },
      },
      {
        title: { fr: 'Acte signé et scellé', en: 'Signed and sealed deed' },
        body: {
          fr: 'Signature électronique et sceau du Notaire vérifiés à chaque ouverture, empreinte SHA-256, numéro de répertoire automatique, export Word et PDF/A, signature électronique du client avec certificat.',
          en: 'The notary’s electronic signature and seal checked at every opening, SHA-256 fingerprint, automatic register number, Word and PDF/A export, client e-signature with certificate.',
        },
      },
      {
        title: { fr: 'Gestion électronique des documents', en: 'Document management' },
        body: {
          fr: 'Pièces numérisées classées par dossier, reconnaissance de texte (OCR), scanner ou photo du téléphone, pièces demandées au client et déposées depuis son espace, fichiers chiffrés.',
          en: 'Scanned documents filed by case, text recognition (OCR), scanner or phone photo, documents requested from the client and uploaded from their portal, encrypted files.',
        },
      },
      {
        title: { fr: 'Comptabilité notariale SYSCOHADA', en: 'SYSCOHADA notarial accounting' },
        body: {
          fr: 'Partie double, journaux, grand livre, balance et états ; recettes de l’étude séparées des fonds clients ; pièces lues par OCR ; provisions payées en ligne par Mobile Money ou carte.',
          en: 'Double-entry bookkeeping, journals, general ledger, trial balance and statements; practice income kept apart from client funds; receipts read by OCR; retainers paid online by Mobile Money or card.',
        },
      },
      {
        title: { fr: 'Fonds clients et rapprochements', en: 'Client funds and reconciliations' },
        body: {
          fr: 'Sous-compte client par dossier, aucun débours au-delà de la provision, rapprochement des fonds de tiers avec le compte 4671, rapprochements bancaire et de caisse.',
          en: 'A client sub-account per file, no disbursement beyond the available retainer, third-party funds reconciled with account 4671, bank and cash reconciliations.',
        },
      },
      {
        title: { fr: 'Espace client', en: 'Client portal' },
        body: {
          fr: 'Le client suit ses dossiers, dépose ses pièces, signe sa lettre de mission avec un code à usage unique et paie ses provisions depuis son téléphone ; notifications par e-mail, SMS ou WhatsApp.',
          en: 'Clients follow their files, upload documents, sign their engagement letter with a one-time code and pay retainers from their phone; notifications by email, SMS or WhatsApp.',
        },
      },
      {
        title: { fr: 'Sécurité et conformité', en: 'Security and compliance' },
        body: {
          fr: 'Double authentification, piste d’audit chaînée et inaltérable, sauvegardes chiffrées AES-256-GCM copiées chaque heure hors de l’ordinateur, droits configurables par rôle.',
          en: 'Two-factor authentication, a chained and tamper-proof audit trail, AES-256-GCM encrypted backups copied off the computer every hour, configurable role-based permissions.',
        },
      },
      {
        title: { fr: 'Informatique décisionnelle (option)', en: 'Business intelligence (option)' },
        body: {
          fr: 'Tableaux de bord interactifs, questions en langage courant avec une réponse rédigée, alertes et prévisions, sur les données de l’étude, Excel, Mobile Money ou Comptia. Les données restent sur votre serveur.',
          en: 'Interactive dashboards, plain-language questions with a written answer, alerts and forecasts, on practice data, Excel, Mobile Money or Comptia. Your data stays on your server.',
        },
      },
      {
        title: { fr: 'Hors ligne et multilingue', en: 'Offline and multilingual' },
        body: {
          fr: 'Tout fonctionne sur le réseau de l’étude même sans Internet ; interface en français, anglais, espagnol et arabe, montants dans la monnaie de l’étude.',
          en: 'Everything runs on the practice network even without the internet; interface in French, English, Spanish and Arabic, amounts in the practice’s currency.',
        },
      },
    ],
    gallery: [
      { src: single('prenim-notary-erp', '03-tableau-de-bord'), caption: { fr: 'Tableau de bord', en: 'Dashboard' } },
      { src: single('prenim-notary-erp', '05-dossier'), caption: { fr: 'Fiche dossier', en: 'Case file' } },
      { src: single('prenim-notary-erp', '06-redaction-acte'), caption: { fr: 'Rédaction guidée d’un acte', en: 'Guided deed drafting' } },
      { src: single('prenim-notary-erp', '08-acte-signe'), caption: { fr: 'Acte signé et scellé', en: 'Signed and sealed deed' } },
      { src: single('prenim-notary-erp', '09-ged'), caption: { fr: 'Gestion électronique des documents', en: 'Document management' } },
      { src: single('prenim-notary-erp', '20-comptabilite'), caption: { fr: 'Comptabilité SYSCOHADA', en: 'SYSCOHADA accounting' } },
      { src: single('prenim-notary-erp', '32-rapprochement-fonds'), caption: { fr: 'Rapprochement des fonds clients', en: 'Client funds reconciliation' } },
      { src: single('prenim-notary-erp', '45-portail-accueil'), caption: { fr: 'Espace client', en: 'Client portal' } },
      { src: single('prenim-notary-erp', '12-audit'), caption: { fr: 'Piste d’audit', en: 'Audit trail' } },
      { src: single('prenim-notary-erp', '14-langue-ar'), caption: { fr: 'Interface en arabe', en: 'Arabic interface' } },
    ],
    deployment: DEPLOY_ERP,
    documents: [
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/prenim-notary-erp/plaquette.fr.pdf`, en: `${MEDIA}/prenim-notary-erp/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'nedjaty-erp',
    name: 'Nedjaty ERP',
    category: { fr: 'Cabinets d’avocats', en: 'Law firms' },
    tagline: { fr: 'Le bouclier numérique des cabinets d’avocats', en: 'The digital shield for law firms' },
    summary: {
      fr: 'Dossiers, délais de procédure, temps passé, facturation, fonds de tiers et comptabilité OHADA dans un seul logiciel, fiable même sans Internet, pour l’avocat seul comme pour le cabinet d’associés.',
      en: 'Files, procedural deadlines, time tracking, billing, client funds and OHADA accounting in a single piece of software, reliable even without the internet, for sole practitioners and partnerships alike.',
    },
    cover: { fr: single('nedjaty-erp', 'boitier.fr'), en: single('nedjaty-erp', 'boitier.en') },
    theme: { from: '#12294a', to: '#0b1b33', accent: '#e3c06b' },
    stats: [
      { value: '17', label: { fr: 'États de l’espace OHADA', en: 'OHADA member states' } },
      { value: '4', label: { fr: 'langues d’interface', en: 'interface languages' } },
      { value: '3', label: { fr: 'modes d’installation', en: 'deployment modes' } },
    ],
    features: [
      {
        title: { fr: 'Dossiers et contacts', en: 'Files and contacts' },
        body: {
          fr: 'Ouverture avec contrôle des conflits d’intérêts ; parties, instances, chronologie, documents chiffrés et versionnés, modèles d’actes.',
          en: 'File opening with conflict-of-interest checks; parties, proceedings, timeline, encrypted and versioned documents, deed templates.',
        },
      },
      {
        title: { fr: 'Délais de procédure', en: 'Procedural deadlines' },
        body: {
          fr: 'Calcul des délais de recours selon des règles validées par un associé (jours francs, report au jour ouvrable), alertes par niveau et rappels par e-mail.',
          en: 'Appeal deadlines computed from rules approved by a partner (clear days, roll-over to the next working day), tiered alerts and email reminders.',
        },
      },
      {
        title: { fr: 'Agenda et temps passé', en: 'Calendar and time tracking' },
        body: {
          fr: 'Audiences et rendez-vous, rôle d’audience imprimable, renvoi en un clic ; minuteur qui compte même sans réseau, enregistré en temps facturable.',
          en: 'Hearings and appointments, printable court list, adjournment in one click; a timer that keeps counting offline and is saved as billable time.',
        },
      },
      {
        title: { fr: 'Facturation et recouvrement', en: 'Billing and collection' },
        body: {
          fr: 'Provisions, notes d’honoraires en FCFA avec TVA paramétrable, avoirs, règlements par Mobile Money et relances automatiques.',
          en: 'Retainers, fee notes in FCFA with configurable VAT, credit notes, Mobile Money payments and automatic reminders.',
        },
      },
      {
        title: { fr: 'Fonds de tiers', en: 'Client funds' },
        body: {
          fr: 'Compte séquestre distinct, solde par client jamais négatif, suivi affaire par affaire et rapprochement bancaire.',
          en: 'A separate escrow account, a per-client balance that can never go negative, case-by-case tracking and bank reconciliation.',
        },
      },
      {
        title: { fr: 'Comptabilité OHADA', en: 'OHADA accounting' },
        body: {
          fr: 'SYSCOHADA révisé : écritures automatiques, pièces lues et comptabilisées seules, balance, grands livres, bilan, compte de résultat et clôture d’exercice.',
          en: 'Revised SYSCOHADA: automatic entries, receipts read and posted on their own, trial balance, ledgers, balance sheet, income statement and year-end closing.',
        },
      },
      {
        title: { fr: 'Conventions et échanges', en: 'Engagements and exchanges' },
        body: {
          fr: 'Une convention type par mission, honoraires préparés à chaque échéance ; registre inaltérable des échanges avec juridictions, greffes, confrères et huissiers.',
          en: 'A template agreement for each type of engagement, fees prepared at each due date; a tamper-proof register of exchanges with courts, registries, colleagues and bailiffs.',
        },
      },
      {
        title: { fr: 'Espace client et signature', en: 'Client portal and signature' },
        body: {
          fr: 'Le client suit ses dossiers et audiences, dépose ses pièces, paie en ligne et signe ses conventions sur son téléphone avec un code à usage unique.',
          en: 'Clients follow their files and hearings, upload documents, pay online and sign their agreements on their phone with a one-time code.',
        },
      },
      {
        title: { fr: 'Travail hors ligne', en: 'Offline work' },
        body: {
          fr: 'Les dossiers emportés restent consultables au palais ; le temps et les documents saisis sans réseau partent seuls au retour de la connexion, une seule fois.',
          en: 'Files taken along remain available at court; time and documents entered offline are sent automatically, exactly once, when the connection returns.',
        },
      },
      {
        title: { fr: 'Sécurité', en: 'Security' },
        body: {
          fr: 'Documents et sauvegardes chiffrés (AES-256), double authentification, droits par rôle et par dossier, journal d’audit inaltérable, sauvegarde chaque heure.',
          en: 'Encrypted documents and backups (AES-256), two-factor authentication, permissions by role and file, tamper-proof audit log, hourly backups.',
        },
      },
    ],
    gallery: [
      { src: bilingual('nedjaty-erp', '03-tableau-de-bord'), caption: { fr: 'Tableau de bord', en: 'Dashboard' } },
      { src: bilingual('nedjaty-erp', '04-dossiers'), caption: { fr: 'Dossiers', en: 'Files' } },
      { src: bilingual('nedjaty-erp', '06-dossier-delais'), caption: { fr: 'Délais d’un dossier', en: 'File deadlines' } },
      { src: bilingual('nedjaty-erp', '08-agenda'), caption: { fr: 'Agenda', en: 'Calendar' } },
      { src: bilingual('nedjaty-erp', '50-minuteur'), caption: { fr: 'Minuteur', en: 'Timer' } },
      { src: bilingual('nedjaty-erp', '10-factures'), caption: { fr: 'Factures', en: 'Invoices' } },
      { src: bilingual('nedjaty-erp', '13-fonds-de-tiers'), caption: { fr: 'Fonds de tiers', en: 'Client funds' } },
      { src: bilingual('nedjaty-erp', '14-comptabilite-synthese'), caption: { fr: 'Comptabilité', en: 'Accounting' } },
      { src: bilingual('nedjaty-erp', '38-espace-client'), caption: { fr: 'Espace client', en: 'Client portal' } },
      { src: bilingual('nedjaty-erp', '44-hors-ligne'), caption: { fr: 'Travail hors ligne', en: 'Offline work' } },
    ],
    deployment: DEPLOY_ERP,
    documents: [
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/nedjaty-erp/plaquette.fr.pdf`, en: `${MEDIA}/nedjaty-erp/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'comptia-pro',
    name: 'Comptia Pro',
    category: { fr: 'Comptabilité', en: 'Accounting' },
    tagline: { fr: 'La comptabilité SYSCOHADA simple et sûre', en: 'Simple, secure SYSCOHADA accounting' },
    summary: {
      fr: 'La comptabilité SYSCOHADA conforme à l’Acte uniforme OHADA du 26 janvier 2017, avec la DSF officielle de chacun des six pays de la CEMAC produite par le logiciel. Pour les TPE, les PME, les cabinets d’expertise comptable et les groupes.',
      en: 'SYSCOHADA accounting compliant with the OHADA Uniform Act of 26 January 2017, with the official tax return (DSF) of each of the six CEMAC countries produced by the software. For small businesses, SMEs, accounting firms and groups.',
    },
    cover: { fr: single('comptia-pro', 'boitier.fr'), en: single('comptia-pro', 'boitier.en') },
    theme: { from: '#1f3a6e', to: '#14284f', accent: '#e6c06a' },
    stats: [
      { value: '6', label: { fr: 'DSF officielles de la CEMAC', en: 'official CEMAC tax returns' } },
      { value: '4', label: { fr: 'modes d’installation', en: 'deployment modes' } },
      { value: '30', label: { fr: 'jours d’évaluation gratuite', en: 'day free trial' } },
    ],
    features: [
      {
        title: { fr: 'Saisie contrôlée', en: 'Controlled entry' },
        body: {
          fr: 'Équilibre, exercice et numérotation vérifiés en continu, comptes créés en cours de saisie, journaux, grand livre et balance.',
          en: 'Balance, financial year and numbering checked continuously, accounts created on the fly, journals, general ledger and trial balance.',
        },
      },
      {
        title: { fr: 'États financiers et DSF', en: 'Financial statements and DSF' },
        body: {
          fr: 'Bilan, compte de résultat, flux de trésorerie et DSF officielle de chaque pays de la CEMAC, en PDF, Excel, Word et CSV.',
          en: 'Balance sheet, income statement, cash flow and the official DSF of each CEMAC country, in PDF, Excel, Word and CSV.',
        },
      },
      {
        title: { fr: 'Banque et factures', en: 'Bank and invoices' },
        body: {
          fr: 'Rapprochement bancaire automatique, factures lues par scanner ou par photo, même hors ligne.',
          en: 'Automatic bank reconciliation, invoices read by scanner or phone photo, even offline.',
        },
      },
      {
        title: { fr: 'Portail client', en: 'Client portal' },
        body: {
          fr: 'Factures, relevé et échéances en ligne pour vos clients, consultables sur téléphone.',
          en: 'Invoices, statements and due dates online for your clients, viewable on a phone.',
        },
      },
      {
        title: { fr: 'Notes de frais', en: 'Expense claims' },
        body: {
          fr: 'Photo du justificatif, validation ligne par ligne et remboursement.',
          en: 'Photo of the receipt, line-by-line approval and reimbursement.',
        },
      },
      {
        title: { fr: 'Consolidation et immobilisations', en: 'Consolidation and fixed assets' },
        body: {
          fr: 'Comptes consolidés OHADA, multi-devises et multi-sociétés, immobilisations par composants, réévaluation et inventaire des biens.',
          en: 'OHADA consolidated accounts, multi-currency and multi-company, component-based fixed assets, revaluation and asset inventory.',
        },
      },
      {
        title: { fr: 'Édition Cabinet', en: 'Accounting firm edition' },
        body: {
          fr: 'Pour les cabinets d’expertise comptable : dossiers clients jusqu’à l’illimité, DSF de chaque pays et portail pour vos clients.',
          en: 'For accounting firms: up to unlimited client files, the DSF of each country and a portal for your clients.',
        },
      },
      {
        title: { fr: 'Sécurité', en: 'Security' },
        body: {
          fr: 'Base chiffrée, droits par rôle et journal d’audit ; fonctionne sans Internet.',
          en: 'Encrypted database, role-based permissions and audit log; works without the internet.',
        },
      },
    ],
    gallery: [
      { src: single('comptia-pro', 'ecran-saisie'), caption: { fr: 'Saisie des écritures', en: 'Journal entry' } },
      { src: single('comptia-pro', 'ecran-dsf-officielle'), caption: { fr: 'DSF officielle', en: 'Official DSF' } },
      { src: single('comptia-pro', 'ecran-dsf-liasse-bilan'), caption: { fr: 'Liasse : bilan', en: 'Return: balance sheet' } },
      { src: single('comptia-pro', 'ecran-banque'), caption: { fr: 'Rapprochement bancaire', en: 'Bank reconciliation' } },
      { src: single('comptia-pro', 'ecran-immobilisations'), caption: { fr: 'Immobilisations', en: 'Fixed assets' } },
      { src: single('comptia-pro', 'ecran-tiers'), caption: { fr: 'Clients et fournisseurs', en: 'Customers and suppliers' } },
      { src: single('comptia-pro', 'ecran-economies'), caption: { fr: 'Économies réalisées', en: 'Savings achieved' } },
    ],
    deployment: [
      ...DEPLOY_ERP.slice(0, 2),
      {
        title: { fr: 'Hybride', en: 'Hybrid' },
        body: {
          fr: 'Si le réseau ou le serveur s’arrête, les postes continuent puis se resynchronisent.',
          en: 'If the network or the server stops, workstations carry on and then resynchronise.',
        },
      },
      DEPLOY_ERP[2],
    ],
    documents: [
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/comptia-pro/plaquette.fr.pdf`, en: `${MEDIA}/comptia-pro/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'comptia-pro-erp',
    name: 'Comptia Pro ERP',
    category: { fr: 'Gestion d’entreprise', en: 'Business management' },
    tagline: { fr: 'Toute votre entreprise dans un seul logiciel', en: 'Your whole business in one piece of software' },
    summary: {
      fr: 'Comptabilité, paie et RH, stocks, ventes, CRM, projets et caisse dans un seul logiciel conforme à l’Acte uniforme OHADA du 26 janvier 2017. Pilotez toute votre entreprise depuis un même écran.',
      en: 'Accounting, payroll and HR, inventory, sales, CRM, projects and point of sale in a single piece of software compliant with the OHADA Uniform Act of 26 January 2017. Run your whole business from one screen.',
    },
    cover: { fr: single('comptia-pro-erp', 'boitier.fr'), en: single('comptia-pro-erp', 'boitier.en') },
    theme: { from: '#0d4a43', to: '#08332e', accent: '#e6c06a' },
    stats: [
      { value: '6', label: { fr: 'pays de la CEMAC', en: 'CEMAC countries' } },
      { value: '4', label: { fr: 'modes d’installation', en: 'deployment modes' } },
      { value: '30', label: { fr: 'jours d’évaluation gratuite', en: 'day free trial' } },
    ],
    features: [
      {
        title: { fr: 'Comptabilité et DSF', en: 'Accounting and DSF' },
        body: {
          fr: 'Saisie contrôlée, états financiers et DSF officielle de chaque pays de la CEMAC.',
          en: 'Controlled entry, financial statements and the official DSF of each CEMAC country.',
        },
      },
      {
        title: { fr: 'Paie et RH', en: 'Payroll and HR' },
        body: {
          fr: 'Bulletins, déclarations officielles, contrats, congés et portail salarié.',
          en: 'Payslips, statutory returns, contracts, leave and an employee portal.',
        },
      },
      {
        title: { fr: 'Stocks et traçabilité', en: 'Inventory and traceability' },
        body: {
          fr: 'Codes-barres, dépôts multiples, lots et dates de péremption, rappel de lot.',
          en: 'Barcodes, multiple warehouses, batches and expiry dates, batch recall.',
        },
      },
      {
        title: { fr: 'Ventes, CRM et caisse', en: 'Sales, CRM and point of sale' },
        body: {
          fr: 'Prospects et pipeline, tarifs et remises, caisse et paiements Mobile Money.',
          en: 'Leads and pipeline, price lists and discounts, point of sale and Mobile Money payments.',
        },
      },
      {
        title: { fr: 'Projets et approbations', en: 'Projects and approvals' },
        body: {
          fr: 'Budget par phase, temps passés, facturation et circuit d’approbation.',
          en: 'Budget by phase, time spent, invoicing and approval workflow.',
        },
      },
      {
        title: { fr: 'Pilotage', en: 'Management reporting' },
        body: {
          fr: 'Budget, écarts, reporting, comptabilité analytique et trésorerie prévisionnelle.',
          en: 'Budgets, variances, reporting, cost accounting and cash-flow forecasting.',
        },
      },
      {
        title: { fr: 'Sécurité', en: 'Security' },
        body: {
          fr: 'Données chiffrées, droits par rôle et journal d’audit ; fonctionne hors connexion.',
          en: 'Encrypted data, role-based permissions and audit log; works offline.',
        },
      },
    ],
    gallery: [
      { src: single('comptia-pro-erp', 'ecran-ventes'), caption: { fr: 'Ventes', en: 'Sales' } },
      { src: single('comptia-pro-erp', 'ecran-caisse'), caption: { fr: 'Caisse', en: 'Point of sale' } },
      { src: single('comptia-pro-erp', 'ecran-articles'), caption: { fr: 'Articles et stocks', en: 'Items and inventory' } },
      { src: single('comptia-pro-erp', 'ecran-achats'), caption: { fr: 'Achats', en: 'Purchasing' } },
      { src: single('comptia-pro-erp', 'ecran-salaries'), caption: { fr: 'Salariés', en: 'Employees' } },
      { src: single('comptia-pro-erp', 'ecran-bulletins'), caption: { fr: 'Bulletins de paie', en: 'Payslips' } },
      { src: single('comptia-pro-erp', 'ecran-budget'), caption: { fr: 'Budget', en: 'Budget' } },
      { src: single('comptia-pro-erp', 'web-2-tableau-de-bord'), caption: { fr: 'Tableau de bord d’un nouveau dossier', en: 'Dashboard of a new company file' } },
    ],
    deployment: [
      ...DEPLOY_ERP.slice(0, 2),
      {
        title: { fr: 'Hybride', en: 'Hybrid' },
        body: {
          fr: 'Si le réseau ou le serveur s’arrête, les postes continuent puis se resynchronisent.',
          en: 'If the network or the server stops, workstations carry on and then resynchronise.',
        },
      },
      DEPLOY_ERP[2],
    ],
    documents: [
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/comptia-pro-erp/plaquette.fr.pdf`, en: `${MEDIA}/comptia-pro-erp/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'chouty-erp',
    name: 'Chouty ERP',
    category: { fr: 'Gestion commerciale', en: 'Sales and business management' },
    tagline: { fr: 'Toute votre entreprise, dans un seul logiciel', en: 'Your whole business, in one piece of software' },
    summary: {
      fr: 'Ventes, caisse, stocks, achats, comptabilité SYSCOHADA, Mobile Money, WhatsApp et assistant intelligent : la gestion intégrée des PME, commerces, pharmacies et distributeurs de la CEMAC, même sans Internet.',
      en: 'Sales, point of sale, stock, purchasing, SYSCOHADA accounting, Mobile Money, WhatsApp and a smart assistant: integrated management for SMEs, shops, pharmacies and distributors in the CEMAC, even without the internet.',
    },
    cover: { fr: single('chouty-erp', 'boitier.fr'), en: single('chouty-erp', 'boitier.en') },
    theme: { from: '#1f4e79', to: '#142c44', accent: '#e8c97a' },
    stats: [
      { value: '15', label: { fr: 'domaines de gestion couverts', en: 'business areas covered' } },
      { value: '0', label: { fr: 'frais par utilisateur', en: 'per-user fees' } },
      { value: '3', label: { fr: 'langues d’interface', en: 'interface languages' } },
    ],
    features: [
      {
        title: { fr: 'Ventes et relation client', en: 'Sales and customer relations' },
        body: {
          fr: 'Devis, commandes, livraisons et factures enchaînés sans ressaisie ; pipeline commercial, campagnes e-mail et SMS ; le client accepte son devis ou paie sa facture d’un clic.',
          en: 'Quotes, orders, deliveries and invoices linked with no re-keying; sales pipeline, email and SMS campaigns; customers accept a quote or pay an invoice in one click.',
        },
      },
      {
        title: { fr: 'Point de vente', en: 'Point of sale' },
        body: {
          fr: 'La caisse des pharmacies, boutiques et supérettes : ouverture et clôture par comptage, vente au toucher ou à la douchette, tickets en attente, écarts expliqués.',
          en: 'The till for pharmacies, shops and minimarkets: opening and closing by count, touch or barcode sales, parked tickets, explained variances.',
        },
      },
      {
        title: { fr: 'Stocks, achats et production', en: 'Stock, purchasing and production' },
        body: {
          fr: 'Stocks par lot, par emplacement et par dépôt, péremptions suivies, inventaires, appels d’offres et ordres de fabrication.',
          en: 'Stock by batch, location and warehouse, expiry dates tracked, stock counts, tenders and production orders.',
        },
      },
      {
        title: { fr: 'Paiements et messagerie', en: 'Payments and messaging' },
        body: {
          fr: 'Mobile Money MTN, Orange, Airtel et Wave ; règlements rapprochés ; devis, factures et relances envoyés par WhatsApp, e-mail ou SMS.',
          en: 'MTN, Orange, Airtel and Wave Mobile Money; reconciled payments; quotes, invoices and reminders sent by WhatsApp, email or SMS.',
        },
      },
      {
        title: { fr: 'Comptabilité et fiscalité OHADA', en: 'OHADA accounting and tax' },
        body: {
          fr: 'Écritures SYSCOHADA automatiques, déclaration de TVA et état des retenues prêts, liasse et DSF, en franc CFA et en devises.',
          en: 'Automatic SYSCOHADA entries, VAT return and withholding statement ready, financial statements and DSF, in CFA francs and foreign currencies.',
        },
      },
      {
        title: { fr: 'Assistant et analyses', en: 'Assistant and analytics' },
        body: {
          fr: 'Commandes en langage courant, alertes du copilote (ruptures, plafonds de crédit, baisses de marge) et rapports à la demande.',
          en: 'Commands in everyday language, copilot alerts (stock-outs, credit limits, falling margins) and reports on demand.',
        },
      },
      {
        title: { fr: 'Personnalisation et automatisations', en: 'Customisation and automation' },
        body: {
          fr: 'Tableau de bord propre à chaque utilisateur, vues, formules et règles d’automatisation sans programmation.',
          en: 'A dashboard for each user, custom views, formulas and automation rules with no programming.',
        },
      },
      {
        title: { fr: 'Partout, même hors ligne', en: 'Everywhere, even offline' },
        body: {
          fr: 'Ordinateur, tablette et téléphone ; fonctionnement complet sans Internet et synchronisation au retour du réseau, sans double saisie ; utilisateurs illimités.',
          en: 'Computer, tablet and phone; works fully offline and syncs when the network returns, with no double entry; unlimited users.',
        },
      },
    ],
    gallery: [
      { src: bilingual('chouty-erp', 'tableau'), caption: { fr: 'Tableau de bord', en: 'Dashboard' } },
      { src: bilingual('chouty-erp', 'ventes'), caption: { fr: 'Ventes', en: 'Sales' } },
      { src: bilingual('chouty-erp', 'caisse'), caption: { fr: 'Point de vente', en: 'Point of sale' } },
      { src: bilingual('chouty-erp', 'stocks'), caption: { fr: 'Stocks', en: 'Stock' } },
      { src: bilingual('chouty-erp', 'paiement'), caption: { fr: 'Paiement en ligne', en: 'Online payment' } },
      { src: bilingual('chouty-erp', 'compta'), caption: { fr: 'Comptabilité', en: 'Accounting' } },
      { src: bilingual('chouty-erp', 'declarations'), caption: { fr: 'Déclarations', en: 'Tax returns' } },
      { src: bilingual('chouty-erp', 'assistant'), caption: { fr: 'Assistant', en: 'Assistant' } },
      { src: bilingual('chouty-erp', 'analyses'), caption: { fr: 'Analyses', en: 'Analytics' } },
      { src: bilingual('chouty-erp', 'automatisations'), caption: { fr: 'Automatisations', en: 'Automation' } },
    ],
    deployment: [
      {
        title: { fr: 'Poste', en: 'Desktop' },
        body: { fr: 'Le logiciel sur un ordinateur, sans dépendre d’Internet.', en: 'The software on one computer, with no need for the internet.' },
      },
      {
        title: { fr: 'Client / Serveur', en: 'Client / Server' },
        body: { fr: 'Un serveur dans l’entreprise ; postes, tablettes et téléphones s’y connectent.', en: 'One server on your premises; computers, tablets and phones connect to it.' },
      },
      {
        title: { fr: 'En ligne', en: 'Online' },
        body: { fr: 'Chouty hébergé et tenu à jour par Prenim, au mois ou à l’année, accessible depuis partout.', en: 'Chouty hosted and kept up to date by Prenim, monthly or yearly, available anywhere.' },
      },
    ],
    documents: [
      { label: { fr: 'Dossier de présentation (PDF)', en: 'Presentation pack (PDF)' }, href: { fr: `${MEDIA}/chouty-erp/dossier.fr.pdf`, en: `${MEDIA}/chouty-erp/dossier.en.pdf` } },
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/chouty-erp/plaquette.fr.pdf`, en: `${MEDIA}/chouty-erp/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'alkebulan-paie',
    name: 'Alkebulan Paie',
    category: { fr: 'Paie et RH', en: 'Payroll and HR' },
    tagline: { fr: 'La paie juste, partout en CEMAC, même sans réseau', en: 'Accurate payroll, everywhere in the CEMAC, even offline' },
    summary: {
      fr: 'La paie et les RH multi-pays et multi-sociétés de la CEMAC : barèmes des six pays mis à jour par Prenim, bulletins, déclarations et paiements, écriture SYSCOHADA transférée vers Comptia Pro sans ressaisie.',
      en: 'Multi-country, multi-company payroll and HR for the CEMAC: pay scales for six countries kept up to date by Prenim, payslips, returns and payments, SYSCOHADA entry sent to Comptia Pro with no re-keying.',
    },
    cover: { fr: single('alkebulan-paie', 'boitier.fr'), en: single('alkebulan-paie', 'boitier.en') },
    theme: { from: '#14254a', to: '#0c1730', accent: '#e6c06a' },
    stats: [
      { value: '6', label: { fr: 'pays de la CEMAC, barèmes signés', en: 'CEMAC countries, signed pay scales' } },
      { value: '14', label: { fr: 'contrôles avant validation', en: 'checks before approval' } },
      { value: '3', label: { fr: 'langues : FR, EN, ES', en: 'languages: FR, EN, ES' } },
    ],
    features: [
      {
        title: { fr: 'Moteur et conformité CEMAC', en: 'CEMAC engine and compliance' },
        body: {
          fr: 'Barèmes des six pays datés, signés par un expert local et mis à jour automatiquement ; calcul hors connexion, rappels, net vers brut.',
          en: 'Pay scales for six countries, dated, signed by a local expert and updated automatically; offline calculation, back pay, net to gross.',
        },
      },
      {
        title: { fr: 'Paie du mois et contrôles', en: 'Monthly payroll and checks' },
        body: {
          fr: 'Période ouverte, calculée, validée, clôturée et comptabilisée ; 14 contrôles d’anomalies ; la personne qui calcule ne peut pas valider.',
          en: 'Period opened, calculated, approved, closed and posted; 14 anomaly checks; whoever calculates cannot approve.',
        },
      },
      {
        title: { fr: 'Bulletins et déclarations', en: 'Payslips and returns' },
        body: {
          fr: 'Bulletins conformes en français, anglais ou espagnol, livre de paie, déclarations pré-remplies par organisme, DIPE et déclaration annuelle.',
          en: 'Compliant payslips in French, English or Spanish, payroll register, returns pre-filled per body, DIPE and annual return.',
        },
      },
      {
        title: { fr: 'Paiements', en: 'Payments' },
        body: {
          fr: 'Lettres de virement par banque, virements ISO 20022, mobile money par API et paiement en caisse avec billetage.',
          en: 'Transfer letters per bank, ISO 20022 transfers, mobile money by API and cash payment with note breakdown.',
        },
      },
      {
        title: { fr: 'Lien avec Comptia Pro', en: 'Linked to Comptia Pro' },
        body: {
          fr: 'Écriture SYSCOHADA globale ou détaillée transférée sans ressaisie ; masse salariale consolidée du groupe, tous pays confondus.',
          en: 'Summary or detailed SYSCOHADA entry sent with no re-keying; consolidated group payroll across all countries.',
        },
      },
      {
        title: { fr: 'Services aux salariés', en: 'Employee services' },
        body: {
          fr: 'Avance sur salaire acquis, prêts avec taux d’endettement légal, portail mobile, pointage QR, GPS et facial, WhatsApp et Telegram.',
          en: 'Earned wage access, loans within the legal debt ratio, mobile portal, QR, GPS and facial clock-in, WhatsApp and Telegram.',
        },
      },
      {
        title: { fr: 'Simulateur et assistant', en: 'Simulator and assistant' },
        body: {
          fr: 'Brut vers net, net vers brut, coût employeur ; assistant RH et paie en trois langues.',
          en: 'Gross to net, net to gross, employer cost; HR and payroll assistant in three languages.',
        },
      },
      {
        title: { fr: 'Audit et sécurité', en: 'Audit and security' },
        body: {
          fr: 'Journal d’audit infalsifiable, coffre-fort chiffré avec conservation 10 ans, double authentification, droits fins par module et par rôle.',
          en: 'Tamper-proof audit log, encrypted vault with 10-year retention, two-factor authentication, fine-grained rights by module and role.',
        },
      },
    ],
    gallery: [
      { src: bilingual('alkebulan-paie', '03-tableau-de-bord'), caption: { fr: 'Tableau de bord', en: 'Dashboard' } },
      { src: bilingual('alkebulan-paie', '04-salaries'), caption: { fr: 'Salariés', en: 'Employees' } },
      { src: bilingual('alkebulan-paie', '15-periode-calculee'), caption: { fr: 'Paie du mois', en: 'Monthly payroll' } },
      { src: bilingual('alkebulan-paie', '16-controles'), caption: { fr: 'Contrôles', en: 'Checks' } },
      { src: bilingual('alkebulan-paie', '18-bulletin-pdf'), caption: { fr: 'Bulletin', en: 'Payslip' } },
      { src: bilingual('alkebulan-paie', '25-paiements'), caption: { fr: 'Paiements', en: 'Payments' } },
      { src: bilingual('alkebulan-paie', '26-declarations'), caption: { fr: 'Déclarations', en: 'Returns' } },
      { src: bilingual('alkebulan-paie', '30-consolidation'), caption: { fr: 'Consolidation', en: 'Consolidation' } },
      { src: bilingual('alkebulan-paie', '21-simulateur'), caption: { fr: 'Simulateur', en: 'Simulator' } },
      { src: bilingual('alkebulan-paie', '22-assistant'), caption: { fr: 'Assistant RH et paie', en: 'HR and payroll assistant' } },
    ],
    deployment: [
      {
        title: { fr: 'En ligne (SaaS)', en: 'Online (SaaS)' },
        body: { fr: 'Hébergé par Prenim, accessible partout, sans installation.', en: 'Hosted by Prenim, available anywhere, nothing to install.' },
      },
      {
        title: { fr: 'Sur votre serveur', en: 'On your server' },
        body: { fr: 'Installé chez vous, sur votre réseau.', en: 'Installed on your premises, on your network.' },
      },
      {
        title: { fr: 'Sur un poste Windows', en: 'On a Windows workstation' },
        body: { fr: 'Service autonome qui fonctionne sans Internet.', en: 'A standalone service that works without the internet.' },
      },
    ],
    documents: [
      { label: { fr: 'Dossier de présentation (PDF)', en: 'Presentation pack (PDF)' }, href: { fr: `${MEDIA}/alkebulan-paie/dossier.fr.pdf`, en: `${MEDIA}/alkebulan-paie/dossier.en.pdf` } },
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/alkebulan-paie/plaquette.fr.pdf`, en: `${MEDIA}/alkebulan-paie/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'prenim-ihealth',
    name: 'Prenim iHealth',
    category: { fr: 'Santé', en: 'Healthcare' },
    tagline: { fr: 'Système d’information hospitalier, du cabinet médical à l’hôpital', en: 'Hospital information system, from medical practice to hospital' },
    summary: {
      fr: 'Accueil, consultations, soins, laboratoire, pharmacie, hospitalisation et caisse dans un seul logiciel, conçu pour les établissements de santé de la CEMAC. Il fonctionne sur le réseau local, sans Internet, et rien n’est à installer sur les postes.',
      en: 'Reception, consultations, nursing, laboratory, pharmacy, inpatients and billing in a single piece of software, built for healthcare facilities in the CEMAC area. It runs on the local network without the internet, with nothing to install on workstations.',
    },
    cover: { fr: single('prenim-ihealth', 'boitier.fr'), en: single('prenim-ihealth', 'boitier.en') },
    theme: { from: '#8b1a1a', to: '#6e1414', accent: '#e6c06a' },
    stats: [
      { value: '0', label: { fr: 'installation sur les postes', en: 'installs on workstations' } },
      { value: '∞', label: { fr: 'utilisateurs sur le pack Hôpital', en: 'users on the Hospital pack' } },
      { value: '3', label: { fr: 'langues d’interface', en: 'interface languages' } },
    ],
    features: [
      {
        title: { fr: 'Dossier patient unique', en: 'Single patient record' },
        body: {
          fr: 'Accueil, rendez-vous, file d’attente et dossier partagé par tous les services, avec détection des doublons.',
          en: 'Reception, appointments, queue and a record shared by every department, with duplicate detection.',
        },
      },
      {
        title: { fr: 'Consultations et soins', en: 'Consultations and nursing' },
        body: {
          fr: 'Consultation, constantes, ordonnance électronique avec QR code, plans de soins, transmissions et double contrôle infirmier.',
          en: 'Consultation, vital signs, electronic prescription with QR code, care plans, handovers and nurse double-checking.',
        },
      },
      {
        title: { fr: 'Urgences, maternité et bloc', en: 'Emergency, maternity and theatre' },
        body: {
          fr: 'Tri et tableau des urgences, suivi de grossesse et partogramme, programmation et check-list du bloc opératoire.',
          en: 'Emergency triage and board, pregnancy follow-up and partograph, theatre scheduling and checklist.',
        },
      },
      {
        title: { fr: 'Laboratoire, imagerie et pharmacie', en: 'Laboratory, imaging and pharmacy' },
        body: {
          fr: 'Demandes et résultats d’examens, visionneuse d’images et PACS, vérification des ordonnances à la pharmacie.',
          en: 'Test orders and results, image viewer and PACS, prescription checks at the pharmacy.',
        },
      },
      {
        title: { fr: 'Hospitalisation', en: 'Inpatients' },
        body: {
          fr: 'Tableau des lits en temps réel, admissions, transferts, sorties et comptes rendus.',
          en: 'Real-time bed board, admissions, transfers, discharges and reports.',
        },
      },
      {
        title: { fr: 'Caisse et Mobile Money', en: 'Billing and Mobile Money' },
        body: {
          fr: 'Encaissement au guichet et par Mobile Money, factures, avoirs, clôture de session et rapport Z.',
          en: 'Payment at the desk and by Mobile Money, invoices, credit notes, session closing and Z report.',
        },
      },
      {
        title: { fr: 'Paiement par un proche', en: 'Payment by a relative' },
        body: {
          fr: 'Un proche éloigné règle les soins à distance grâce à un code QR ; l’eCarnet du patient et les rappels par SMS et WhatsApp complètent le suivi.',
          en: 'A relative far away pays for care remotely with a QR code; the patient eCarnet and SMS and WhatsApp reminders complete the follow-up.',
        },
      },
      {
        title: { fr: 'Multi-sites et pilotage', en: 'Multi-site and reporting' },
        body: {
          fr: 'Chaque site garde son serveur, continue seul et se synchronise avec les autres ; informatique décisionnelle avec tendances, prévisions et objectifs.',
          en: 'Each site keeps its own server, carries on alone and syncs with the others; business intelligence with trends, forecasts and targets.',
        },
      },
    ],
    gallery: [
      { src: bilingual('prenim-ihealth', '04-accueil'), caption: { fr: 'Accueil', en: 'Home' } },
      { src: bilingual('prenim-ihealth', '23-dossier-patient'), caption: { fr: 'Dossier patient', en: 'Patient record' } },
      { src: bilingual('prenim-ihealth', '43-consultation'), caption: { fr: 'Consultation', en: 'Consultation' } },
      { src: bilingual('prenim-ihealth', '161-tableau-lits'), caption: { fr: 'Tableau des lits', en: 'Bed board' } },
      { src: bilingual('prenim-ihealth', '103-encaissement'), caption: { fr: 'Encaissement', en: 'Payment' } },
      { src: bilingual('prenim-ihealth', '104-mobile-money'), caption: { fr: 'Paiement Mobile Money', en: 'Mobile Money payment' } },
      { src: bilingual('prenim-ihealth', '242-code-qr-proche'), caption: { fr: 'Paiement par un proche', en: 'Payment by a relative' } },
      { src: bilingual('prenim-ihealth', '265-bi-tableau'), caption: { fr: 'Informatique décisionnelle', en: 'Business intelligence' } },
    ],
    deployment: [
      {
        title: { fr: 'Client / Serveur', en: 'Client / Server' },
        body: {
          fr: 'Un serveur dans l’établissement ; les postes ouvrent iHealth dans le navigateur, sans rien installer.',
          en: 'One server in the facility; workstations open iHealth in the browser, with nothing to install.',
        },
      },
      {
        title: { fr: 'Multi-sites', en: 'Multi-site' },
        body: {
          fr: 'Chaque site garde son serveur, continue seul et se synchronise avec les autres.',
          en: 'Each site keeps its own server, carries on alone and syncs with the others.',
        },
      },
      {
        title: { fr: 'En ligne', en: 'Cloud' },
        body: {
          fr: 'Instance en ligne hébergée par Prenim, en abonnement.',
          en: 'An online instance hosted by Prenim, on subscription.',
        },
      },
    ],
    documents: [],
  },
  {
    slug: 'finsight',
    name: 'FinSight',
    category: { fr: 'Banque', en: 'Banking' },
    tagline: { fr: 'La vie financière de chaque client, racontée comme un film', en: 'The true story behind every client' },
    summary: {
      fr: 'FinSight transforme les données de votre core banking en une histoire claire, animée et décisionnelle, pour vos directeurs, vos agences et votre conformité. 100 % dans votre réseau, sans aucune connexion à Internet.',
      en: 'FinSight turns your core banking data into a clear, animated story that supports decisions, for your directors, your branches and your compliance team. 100% inside your network, with no internet connection at all.',
    },
    cover: { fr: single('finsight', 'boitier.fr'), en: single('finsight', 'boitier.en') },
    theme: { from: '#123f6e', to: '#0b1628', accent: '#5fd0db' },
    stats: [
      { value: '< 3 s', label: { fr: 'pour ouvrir l’histoire d’un client', en: 'to open a client’s full story' } },
      { value: '18', label: { fr: 'graphiques vivants', en: 'live charts' } },
      { value: '0', label: { fr: 'connexion vers l’extérieur', en: 'outbound connections' } },
    ],
    features: [
      {
        title: { fr: 'Story Mode', en: 'Story Mode' },
        body: {
          fr: 'Un directeur choisit un client : toute sa vie dans la banque s’affiche. Un bouton Lecture déroule la timeline comme un film, et les six graphiques se tracent au fil de l’histoire.',
          en: 'A director picks a client and their whole life at the bank appears. A Play button runs the timeline like a film, and six charts draw themselves as the story unfolds.',
        },
      },
      {
        title: { fr: 'Une timeline qui parle', en: 'A timeline that speaks' },
        body: {
          fr: 'Ouvertures, dépôts significatifs, crédits, impayés, alertes AML, opportunités : chaque événement est détecté et coloré selon sa nature, avec un récit rédigé localement.',
          en: 'Account openings, significant deposits, loans, arrears, AML alerts, opportunities: each event is detected and colour-coded, with a narrative written locally.',
        },
      },
      {
        title: { fr: 'Décision de crédit', en: 'Credit decisions' },
        body: {
          fr: 'Pré-score expliqué en six facteurs, taux d’endettement avant et après le projet, capacité restante, niveau de délégation et projection des remboursements.',
          en: 'A pre-score explained by six factors, debt ratio before and after the project, remaining capacity, delegation level and repayment projection.',
        },
      },
      {
        title: { fr: 'Portefeuille de la direction', en: 'Portfolio for management' },
        body: {
          fr: 'Clients actifs, dépôts consolidés, crédits et contentieux, carte thermique des agences, top 10 des clients et centre d’alertes.',
          en: 'Active clients, consolidated deposits, loans and litigation, branch heat map, top 10 clients and alert centre.',
        },
      },
      {
        title: { fr: 'Conformité AML', en: 'AML compliance' },
        body: {
          fr: 'Score d’anomalie de 0 à 100 par client, alertes justifiées, rapport AML pré-rempli au format GABAC.',
          en: 'Anomaly score from 0 to 100 per client, justified alerts, AML report pre-filled in GABAC format.',
        },
      },
      {
        title: { fr: 'Rapport pour le comité', en: 'Committee report' },
        body: {
          fr: 'Story Report PDF signé (SHA-256), protégé par mot de passe si besoin : graphiques, récit, événements, crédits et alertes.',
          en: 'A signed Story Report PDF (SHA-256), password-protected if needed: charts, narrative, events, loans and alerts.',
        },
      },
      {
        title: { fr: 'Sécurité bancaire', en: 'Banking-grade security' },
        body: {
          fr: 'Lecture seule du core banking par des vues SQL que vous maîtrisez, journal d’audit signé conservé 10 ans (COBAC), 5 profils, AES-256 et TLS 1.3.',
          en: 'Read-only access to the core banking system through SQL views you control, signed audit log kept 10 years (COBAC), 5 profiles, AES-256 and TLS 1.3.',
        },
      },
      {
        title: { fr: 'Recherche intelligente', en: 'Smart search' },
        body: {
          fr: 'Nom, compte ou téléphone : FinSight retrouve « Koumaye » quand on tape « Koumaié », en moins d’une demi-seconde.',
          en: 'Name, account or phone: FinSight finds “Koumaye” when you type “Koumaié”, in under half a second.',
        },
      },
    ],
    gallery: [
      { src: bilingual('finsight', '02-tableau-de-bord'), caption: { fr: 'Tableau de bord de la direction', en: 'Management dashboard' } },
      { src: bilingual('finsight', '04-fiche-client'), caption: { fr: 'Fiche client', en: 'Client record' } },
      { src: bilingual('finsight', '05-story-mode'), caption: { fr: 'Story Mode', en: 'Story Mode' } },
      { src: bilingual('finsight', '06-story-lecture'), caption: { fr: 'Lecture de la timeline', en: 'Timeline playback' } },
      { src: bilingual('finsight', '03-recherche'), caption: { fr: 'Recherche intelligente', en: 'Smart search' } },
      { src: bilingual('finsight', '14-studio-decision'), caption: { fr: 'Studio de décision crédit', en: 'Credit decision studio' } },
      { src: bilingual('finsight', '15-studio-graphiques'), caption: { fr: 'Graphiques du studio', en: 'Studio charts' } },
      { src: bilingual('finsight', '17-recouvrement'), caption: { fr: 'Recouvrement anticipé', en: 'Early collection' } },
    ],
    deployment: [
      {
        title: { fr: 'Standalone', en: 'Standalone' },
        body: {
          fr: 'Agence isolée ou poste unique : installateur Windows, base embarquée.',
          en: 'Isolated branch or single workstation: Windows installer, embedded database.',
        },
      },
      {
        title: { fr: 'Client-Serveur', en: 'Client-Server' },
        body: {
          fr: 'Siège et agences en réseau local : serveur Docker, accès par navigateur.',
          en: 'Head office and branches on a local network: Docker server, browser access.',
        },
      },
      {
        title: { fr: 'Multi-site', en: 'Multi-site' },
        body: {
          fr: 'Réseau d’agences sur WAN privé : serveur central, VPN interne. Compatible PostgreSQL, MySQL, SQL Server, Oracle et SQLite.',
          en: 'Branch network over a private WAN: central server, internal VPN. Works with PostgreSQL, MySQL, SQL Server, Oracle and SQLite.',
        },
      },
    ],
    documents: [
      { label: { fr: 'Dossier de présentation (PDF)', en: 'Presentation pack (PDF)' }, href: { fr: `${MEDIA}/finsight/dossier.fr.pdf`, en: `${MEDIA}/finsight/dossier.en.pdf` } },
      { label: { fr: 'Plaquette A4 (PDF)', en: 'A4 brochure (PDF)' }, href: { fr: `${MEDIA}/finsight/plaquette.fr.pdf`, en: `${MEDIA}/finsight/plaquette.en.pdf` } },
    ],
  },
  {
    slug: 'fadel',
    name: 'FaDel Delivery',
    category: { fr: 'Livraison et mobilité', en: 'Delivery and mobility' },
    tagline: { fr: 'La super-application de livraison de N’Djamena', en: 'The delivery super-app of N’Djamena' },
    summary: {
      fr: 'Repas, courses, colis, taxi et pharmacie dans une seule application. Conçue et développée par Prenim, FaDel est une super-application de livraison pensée pour la zone CEMAC, avec N’Djamena comme ville pilote.',
      en: 'Meals, groceries, parcels, rides and pharmacy in a single app. Designed and built by Prenim, FaDel is a delivery super-app made for the CEMAC area, piloted in N’Djamena.',
    },
    cover: { fr: '/images/fadel/banner-1.webp', en: '/images/fadel/banner-1.webp' },
    theme: { from: '#e8590c', to: '#9a3412', accent: '#fde68a' },
    // Banniere tres large : remplie plutot que contenue, pour epouser la carte.
    coverFit: 'cover',
    externalUrl: 'https://www.fadel-delivery.com',
    stats: [
      { value: '8', label: { fr: 'services dans une application', en: 'services in one app' } },
      { value: 'N’Djamena', label: { fr: 'ville pilote', en: 'pilot city' } },
      { value: 'iOS · Android', label: { fr: 'et site web', en: 'and web' } },
    ],
    features: [
      {
        title: { fr: 'FaDel Food', en: 'FaDel Food' },
        body: {
          fr: 'Les restaurants de N’Djamena, leurs cartes et leurs prix en FCFA, commande et suivi de la livraison.',
          en: 'N’Djamena’s restaurants, their menus and prices in FCFA, ordering and delivery tracking.',
        },
      },
      {
        title: { fr: 'Supermarché', en: 'Groceries' },
        body: {
          fr: 'Rayons, promotions et panier, livrés à domicile.',
          en: 'Aisles, offers and basket, delivered to the door.',
        },
      },
      {
        title: { fr: 'Colis express', en: 'Express parcels' },
        body: {
          fr: 'Envoi en ville, même depuis chez un tiers, avec suivi du coursier.',
          en: 'City delivery, even from a third party’s address, with courier tracking.',
        },
      },
      {
        title: { fr: 'FaDel Move', en: 'FaDel Move' },
        body: {
          fr: 'Taxi : point de départ, destination, prix et paiement dans l’application.',
          en: 'Rides: pick-up, destination, price and payment in the app.',
        },
      },
      {
        title: { fr: 'FaDel Care', en: 'FaDel Care' },
        body: {
          fr: 'Pharmacie : envoi de l’ordonnance et pharmacies de garde.',
          en: 'Pharmacy: send your prescription and find on-duty pharmacies.',
        },
      },
      {
        title: { fr: 'Gaz, déménagement et fret', en: 'Gas, removals and freight' },
        body: {
          fr: 'Bouteilles de gaz domestique, camions de déménagement et transport de marchandises.',
          en: 'Cooking gas cylinders, removal trucks and freight transport.',
        },
      },
    ],
    gallery: [1, 2, 3, 4, 5, 6, 7].map((n, i) => ({
      src: `/images/fadel/screen-${n}.webp`,
      portrait: true,
      caption: [
        { fr: 'Accueil', en: 'Home' },
        { fr: 'Nourriture', en: 'Food' },
        { fr: 'Carte d’un restaurant', en: 'Restaurant menu' },
        { fr: 'Supermarché', en: 'Groceries' },
        { fr: 'Colis', en: 'Parcels' },
        { fr: 'Taxi', en: 'Rides' },
        { fr: 'Pharmacie', en: 'Pharmacy' },
      ][i],
    })),
    deployment: [],
    documents: [
      { label: { fr: 'Plaquette A4 (PDF, en français)', en: 'A4 brochure (PDF, in French)' }, href: { fr: `${MEDIA}/fadel/plaquette.fr.pdf`, en: `${MEDIA}/fadel/plaquette.fr.pdf` } },
    ],
  },
];

export const findProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
